<div class="manage-locations">
  <div *ngIf="pageLoading" class="d-flex justify-content-center align-items-center" style="height: 80vh">
    <div class="spinner-border text-primary" role="status"></div>
    <span class="ms-3 text-muted fw-bold">Loading your logistics network...</span>
  </div>

  <div *ngIf="!pageLoading && !isInitialize && zones.length === 0" class="d-flex flex-column align-items-center justify-content-center py-5">
    <div class="card border-0 shadow-lg rounded-5 p-4 p-md-5 text-center bg-white" style="max-width: 600px">
      <div class="bg-primary-subtle rounded-circle d-flex align-items-center justify-content-center mb-4 mx-auto" style="width: 80px; height: 80px; width: 100px; height: 100px">
        <i class="bi bi-geo-fill fs-1 text-primary"></i>
      </div>
      <h3 class="fw-bold text-dark mb-3">Setup Your Logistics Network</h3>
      <p class="text-muted mb-4 px-3">Define delivery regions to enable automated rate calculations.</p>
      <div class="d-grid gap-3 d-sm-flex justify-content-center">
        <button class="btn btn-primary btn-lg fw-bold px-4 py-3 rounded-4 shadow-sm" (click)="initializePopularZones()" [disabled]="isLoading">
          <span *ngIf="isLoading" class="spinner-border spinner-border-sm me-2"></span>
          <i *ngIf="!isLoading" class="bi bi-magic me-2"></i>
          {{ isLoading ? 'Initializing...' : 'Initialize Popular Zones' }}
        </button>
      </div>
    </div>
  </div>

  <div class="row g-4" *ngIf="!pageLoading && (isInitialize || zones.length > 0)">
    <div class="col-12 col-md-5 col-lg-4">
      <header class="section-head">
        <h5 class="section-title">Create Zone</h5>
        <p class="section-note">Group suburbs into a custom region.</p>
      </header>
      <div class="create-zone">
        <div class="d-flex flex-column gap-4">
          <div>
            <label class="form-label fw-bold small text-secondary text-uppercase">Zone Name</label>
            <div class="d-flex flex-column gap-2">
              <ng-select
                #zoneSelect
                [items]="zoneOptions"
                bindLabel="label"
                placeholder="Search e.g. Sydney Metro"
                [(ngModel)]="selectedZone"
                (change)="onZoneSelect($event); zoneSelect.searchTerm = ''"
                class="custom-ng-select"
                [typeahead]="zoneSearchTerm$"
                [loading]="isLoadingZoneOptions"
                [clearable]="true"
              >
                <ng-template ng-notfound-tmp let-searchTerm="searchTerm">
                  <div class="p-3 text-center">
                    <p class="text-muted small mb-2">No zones found for "{{ searchTerm }}"</p>
                    <button type="button" class="btn btn-sm btn-outline-primary fw-bold" (click)="manualCreateZone(searchTerm)">
                      <i class="bi bi-plus-lg me-1"></i>
                      Create Custom Zone
                    </button>
                  </div>
                </ng-template>
              </ng-select>
            </div>
          </div>

          <div>
            <label class="form-label fw-bold text-secondary small text-uppercase mb-2 d-flex justify-content-between align-items-center">
              <span>
                Select Suburbs
                <span *ngIf="!isZoneSelected" class="text-danger ms-1" style="font-size: 0.7rem">(Select Zone First)</span>
              </span>

              <button *ngIf="isZoneSelected && !isBulkMode" type="button" class="btn btn-link btn-sm p-0 text-decoration-none fw-bold" (click)="openBulkSuburbModal()">
                <i class="bi bi-file-earmark-arrow-up me-1"></i>
                Bulk Upload
              </button>
            </label>

            <div *ngIf="isBulkMode" class="alert alert-primary d-flex justify-content-between align-items-center p-2 rounded-3 mb-2 border-0 shadow-sm animate-fade-in">
              <div class="d-flex align-items-center gap-2 overflow-hidden">
                <i class="bi bi-filetype-csv fs-4 text-primary"></i>
                <div class="overflow-hidden">
                  <div class="text-truncate small fw-bold text-dark">{{ uploadedFile?.name }}</div>
                  <div class="extra-small text-primary fw-bold" style="font-size: 0.7rem">FILE ATTACHED</div>
                </div>
              </div>
              <button type="button" class="btn btn-sm btn-outline-danger border-0" (click)="removeBulkFile()">
                <i class="bi bi-trash"></i>
              </button>
            </div>

            <div [class.opacity-50]="!isZoneSelected || isBulkMode" class="transition-all">
              <ng-select
                #suburbSelect
                [items]="suburbOptions"
                [multiple]="true"
                [closeOnSelect]="false"
                [readonly]="!isZoneSelected || isBulkMode"
                bindLabel="label"
                [placeholder]="isBulkMode ? 'Using suburbs from attached CSV' : 'Search and select suburbs...'"
                [(ngModel)]="stagedSuburbs"
                [typeahead]="suburbSearchTerm$"
                [loading]="isLoadingSuburbOptions"
                class="custom-ng-select"
                [clearable]="!isBulkMode"
                (change)="suburbSelect.searchTerm = ''"
              >
                <ng-template ng-multi-label-tmp let-items="items" let-clear="clear">
                  <div class="d-flex flex-wrap gap-1 p-1">
                    <div class="badge suburb-badge d-flex align-items-center bg-white text-dark border p-2 rounded-3 fw-medium shadow-sm transition-all" *ngFor="let item of items">
                      {{ item.label }}
                      <span class="ms-2 cursor-pointer text-danger" (click)="clear(item)" *ngIf="!isBulkMode"><i class="bi bi-x-circle-fill opacity-75"></i></span>
                    </div>
                  </div>
                </ng-template>
              </ng-select>
            </div>
          </div>

          <button type="button" class="btn btn-primary fw-bold py-2 shadow-sm" (click)="createZone()" [disabled]="isLoading || !selectedZone || activeEditZoneId !== null">
            <span *ngIf="isLoading" class="spinner-border spinner-border-sm me-1"></span>
            Update Zone
          </button>
        </div>
      </div>
    </div>

    <div class="col-12 col-md-7 col-lg-8">
      <header class="section-head section-head--split">
        <h5 class="section-title">
          Your Logistics Zones
          <span class="zone-count">{{ paginationInfo?.count || zones.length }}</span>
        </h5>
        <div class="d-flex gap-2">
          <div class="position-relative">
            <i class="bi bi-search position-absolute top-50 start-0 translate-middle-y ms-3 text-muted"></i>
            <input
              type="text"
              class="form-control form-control-sm border-0 shadow-sm rounded-pill ps-5 pe-5 w-auto"
              placeholder="Search zones..."
              [(ngModel)]="search"
              (ngModelChange)="zoneListingSearch$.next($event)"
            />
            <button *ngIf="search" type="button" class="btn btn-link p-0 position-absolute top-50 end-0 translate-middle-y me-3 text-muted" (click)="search = ''; zoneListingSearch$.next('')">
              <i class="bi bi-x-lg"></i>
            </button>
          </div>
          <select
            class="form-select form-select-sm border-0 shadow-sm rounded-pill ps-3 pe-5 w-auto"
            [(ngModel)]="currentSort"
            (change)="onSortChange(currentSort)"
            style="appearance: none; -webkit-appearance: none; background-position: right 1rem center"
          >
            <option value="-updated_at">Latest Update</option>
            <option value="-created_at">Newest First</option>
            <option value="created_at">Oldest First</option>
            <option value="name">Name (A-Z)</option>
            <option value="-name">Name (Z-A)</option>
          </select>
        </div>
      </header>

      <div class="position-relative min-vh-50">
        <div *ngIf="isRefetching" class="position-absolute top-0 start-0 w-100 h-100 d-flex justify-content-center align-items-start pt-5 bg-white bg-opacity-75 z-3">
          <div class="text-center bg-white p-3 rounded-4 shadow-sm border">
            <div class="spinner-border text-primary" role="status" style="width: 1.5rem; height: 1.5rem"></div>
            <p class="mt-2 mb-0 text-muted small fw-bold">Updating...</p>
          </div>
        </div>

        <div [class.opacity-50]="isRefetching">
          <div *ngFor="let zone of zones">
            <div class="card border-primary shadow mb-3 border-start border-4 animate-slide-up" *ngIf="zone.isEditing">
              <div class="card-body p-3">
                <div class="d-flex justify-content-between align-items-center mb-3">
                  <h5 class="mb-0 fw-bold text-dark">{{ zone.name }}</h5>
                  <div class="d-flex gap-2">
                    <button class="btn btn-light btn-sm px-3 rounded-pill border" (click)="cancelEdit(zone)">Cancel</button>
                    <button class="btn btn-primary btn-sm px-3 rounded-pill d-flex align-items-center gap-2" (click)="saveZoneChanges(zone)" [disabled]="isInlineLoading">
                      <span *ngIf="isInlineLoading" class="spinner-border spinner-border-sm"></span>
                      {{ isInlineLoading ? 'Saving...' : 'Save Changes' }}
                    </button>
                  </div>
                </div>
                <ng-select
                  #inlineSuburbSelect
                  [items]="inlineSuburbOptions"
                  [multiple]="true"
                  [closeOnSelect]="false"
                  bindLabel="name"
                  [(ngModel)]="zone.suburbs"
                  [typeahead]="inlineSuburbSearchTerm$"
                  [loading]="isLoadingInlineSuburbs"
                  class="custom-ng-select"
                  (change)="inlineSuburbSelect.searchTerm = ''"
                ></ng-select>
              </div>
            </div>

            <div class="card border-0 shadow-sm mb-3 overflow-hidden" *ngIf="!zone.isEditing">
              <div class="card-body p-3">
                <div class="d-flex justify-content-between align-items-start mb-2">
                  <div>
                    <span *ngIf="zone.suburbs.length > 0" class="badge mb-1 border bg-success-subtle text-success">Live</span>
                    <div class="d-flex align-items-center gap-2">
                      <h5 class="mb-0 fw-bold text-dark">{{ zone.name }}</h5>
                    </div>
                  </div>
                  <div class="btn-group shadow-sm">
                    <button class="btn btn-white btn-sm border text-primary" (click)="toggleEdit(zone)"><i class="bi bi-pencil-square"></i></button>
                    <button class="btn btn-white btn-sm border text-danger" (click)="deleteZone(zone.id)"><i class="bi bi-trash"></i></button>
                  </div>
                </div>
                <div class="d-flex flex-wrap gap-2 mt-2">
                  <span
                    *ngFor="let sub of zone.showAllSuburbs ? zone.suburbs : zone.suburbs.slice(0, 10)"
                    class="badge suburb-badge rounded-pill bg-light text-secondary border px-3 py-2 transition-all"
                  >
                    {{ sub.name }}
                  </span>
                  <button *ngIf="zone.suburbs.length > 10" class="btn btn-link btn-sm p-0 text-decoration-none fw-bold" (click)="zone.showAllSuburbs = !zone.showAllSuburbs">
                    <i class="bi" [ngClass]="zone.showAllSuburbs ? 'bi-dash-lg' : 'bi-plus-lg'"></i>
                    {{ zone.showAllSuburbs ? 'Show Less' : 'Show All (' + zone.suburbs.length + ')' }}
                  </button>
                </div>
              </div>
            </div>
          </div>

          <nav class="mt-5 pb-4" *ngIf="paginationInfo && paginationInfo.total_pages > 1">
            <div class="d-flex justify-content-center align-items-center gap-3">
              <button class="page-link rounded-circle border-0 shadow-sm bg-white p-2" (click)="onPageChange('prev')" [disabled]="!paginationInfo.previous">
                <i class="bi bi-chevron-left"></i>
              </button>
              <span class="fw-bold text-dark">Page {{ paginationInfo.current_page }} of {{ paginationInfo.total_pages }}</span>
              <button class="page-link rounded-circle border-0 shadow-sm bg-white p-2" (click)="onPageChange('next')" [disabled]="!paginationInfo.next">
                <i class="bi bi-chevron-right"></i>
              </button>
            </div>
          </nav>
        </div>
      </div>
    </div>
  </div>
</div>
